/**
 * Key Results automatisch befuellen (V-06 Vorschlag, F-22 Notenziele) - rein
 * und getestet.
 *
 * Statt den Ist-Wert von Hand nachzutragen, zaehlt er erledigte Tage einer
 * Gewohnheit, folgt dem Notenschnitt eines Fachs oder einem Kennwert aus dem
 * Trainingsprotokoll (F-34 D). Geschrieben wird er wie
 * eine Eingabe von Hand (mit Verlauf) - so rechnen alle Fortschrittsanzeigen
 * unveraendert weiter.
 */
import { noteAusPunkten, schnittPunkte } from '@/domain/school/grades';
import { MASS_ARTEN, massText, trainingsWert } from '@/domain/training/kennwerte';
import { ERNAEHRUNG_MASSE, ernaehrungsMassText, ernaehrungsWert, type KennTag } from '@/domain/ernaehrung/kennwerte';
import type { TrainingsProtokoll, Uebung } from '@/domain/training/typen';
import { startOfWeek } from '@/lib/datetime';
import type {
  Goal, Grade, HabitEntry, ID, KeyResult, KeyResultQuelle, LocalDate, Notensystem,
} from '@/domain/types';

export interface AutoDaten {
  habitEntries: HabitEntry[];
  grades: Grade[];
  notensystem: Notensystem;
  /** Nur noetig, wenn ein Key Result am Training haengt (F-34 D). */
  protokolle?: TrainingsProtokoll[];
  bibliothek?: ReadonlyMap<string, Uebung>;
  /** Nur noetig, wenn ein Key Result an der Ernaehrung haengt (F-35 E). */
  ernaehrung?: KennTag[];
}

const eineStelle = (x: number) => Math.round(x * 10) / 10;

/** Der Wert aus der Quelle, `null` wenn es (noch) keinen gibt - dann bleibt der alte. */
export function automatischerWert(kr: KeyResult, goal: Pick<Goal, 'periodStart' | 'periodEnd'>, d: AutoDaten, heute: LocalDate): number | null {
  const q = kr.quelle;
  if (!q) return null;
  if (q.art === 'gewohnheit') {
    const von = q.zeitraum === 'woche' ? startOfWeek(heute) : goal.periodStart;
    const bis = q.zeitraum === 'woche' || heute < goal.periodEnd ? heute : goal.periodEnd;
    return d.habitEntries.filter((e) => e.habitId === q.habitId && e.done && e.date >= von && e.date <= bis).length;
  }
  if (q.art === 'training') {
    const bis = heute < goal.periodEnd ? heute : goal.periodEnd;
    return trainingsWert(q.mass, d.protokolle ?? [], d.bibliothek ?? new Map(), { von: goal.periodStart, bis }, heute);
  }
  if (q.art === 'ernaehrung') return ernaehrungsWert(q.mass, d.ernaehrung ?? [], { von: goal.periodStart, bis: goal.periodEnd }, heute);
  const schnitt = schnittPunkte(d.grades.filter((g) => g.subjectId === q.subjectId));
  if (schnitt === null) return null;
  return eineStelle(d.notensystem === 'punkte' ? schnitt : noteAusPunkten(schnitt));
}

export interface AutoUpdate { goalId: ID; krId: ID; wert: number }

/** Was sich geaendert hat - nur laufende Ziele, erreichte und verworfene bleiben stehen. */
export function abgleich(goals: Goal[], d: AutoDaten, heute: LocalDate): AutoUpdate[] {
  const out: AutoUpdate[] = [];
  for (const g of goals) {
    if (g.status !== 'offen' && g.status !== 'laeuft') continue;
    for (const kr of g.keyResults) {
      const wert = automatischerWert(kr, g, d, heute);
      if (wert !== null && wert !== kr.current) out.push({ goalId: g.id, krId: kr.id, wert });
    }
  }
  return out;
}

/**
 * Quelle setzen und die Art passend machen: Gewohnheiten zaehlen (Anzahl ab
 * 0), Noten sind eine Zahl - Punkte steigern, Noten senken. Ohne Quelle
 * bleibt alles wie es ist, nur von Hand.
 */
export function mitQuelle(kr: KeyResult, quelle: KeyResultQuelle | undefined, notensystem: Notensystem): KeyResult {
  if (!quelle) {
    const rest = { ...kr };
    delete rest.quelle;
    return rest;
  }
  if (quelle.art === 'gewohnheit') {
    return { ...kr, quelle, type: 'anzahl', start: 0, direction: undefined, target: Math.max(1, Math.round(kr.target)), unit: kr.unit || (quelle.zeitraum === 'woche' ? 'mal pro Woche' : 'mal') };
  }
  if (quelle.art === 'training') {
    const a = MASS_ARTEN[quelle.mass.art];
    return { ...kr, quelle, type: 'zahl', direction: a.richtung, unit: a.einheit };
  }
  if (quelle.art === 'ernaehrung') {
    const m = ERNAEHRUNG_MASSE[quelle.mass.art];
    return m.anzahl
      ? { ...kr, quelle, type: 'anzahl', start: 0, direction: undefined, target: Math.max(1, Math.round(kr.target)), unit: m.einheit }
      : { ...kr, quelle, type: 'zahl', direction: 'steigern', unit: m.einheit };
  }
  return notensystem === 'punkte'
    ? { ...kr, quelle, type: 'zahl', direction: 'steigern', unit: 'P' }
    : { ...kr, quelle, type: 'zahl', direction: 'senken', unit: 'Note' };
}

/** "Gewohnheit „Laufen“, im Zielzeitraum" - fuer Karte und Dialog. */
export function quelleText(q: KeyResultQuelle, namen: { habits: Map<ID, string>; subjects: Map<ID, string>; uebungen?: ReadonlyMap<ID, Uebung> }): string {
  if (q.art === 'training') return `Training: ${massText(q.mass, namen.uebungen ?? new Map())}`;
  if (q.art === 'ernaehrung') return `Ernährung: ${ernaehrungsMassText(q.mass)}`;
  if (q.art === 'gewohnheit') {
    return `Gewohnheit „${namen.habits.get(q.habitId) ?? 'gelöscht'}“, ${q.zeitraum === 'woche' ? 'diese Woche' : 'im Zielzeitraum'}`;
  }
  return `Notenschnitt ${namen.subjects.get(q.subjectId) ?? 'Fach gelöscht'}`;
}
